import { PortableTextBlock } from '@portabletext/react';
import { Link } from 'gatsby';
import { useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { Container, Section } from 'components/GridV2';
import CustomPortableText from 'components/rebrand/CustomPortableText';
import TiersCompareTable from 'components/tiers-compare-table/TiersCompareTable';

import { IQueryParams, ISanityCompareTiers } from 'typescriptDefs';

import { trackEvent } from 'helpers';

import { Button } from './Button';
import { LocaleContext } from './Layout';
import { P } from './Typography';

interface Props {
  className?: string;
  heading: PortableTextBlock | PortableTextBlock[];
  pageName: string;
  queryParams?: IQueryParams;
  showAccountBuilderCta?: boolean;
  subheading?: PortableTextBlock | PortableTextBlock[] | string;
  tiers: ISanityCompareTiers;
  toggleBenefits?: boolean;
}

const Tiers = ({
  className,
  heading,
  pageName,
  queryParams,
  showAccountBuilderCta = false,
  subheading,
  tiers,
  toggleBenefits,
}: Props) => (
  <Section className={twMerge('py-64 md:py-96', className)}>
    <Container className="px-32 md:px-0">
      <div className="mb-64 text-center md:mb-70">
        <div
          className={twMerge(
            'tiers-heading mx-auto mb-64 md:w-[65%] md:text-center [&_p]:text-lg md:[&_p]:text-xl',
            subheading && 'mb-16 md:mb-24',
          )}
        >
          <CustomPortableText
            headingStyles={{
              h1: {
                className: 'last-of-type:mb-16 md:last-of-type:mb-24 text-primary-400',
                headingStyle: true,
              },
              h2: {
                className: 'last-of-type:mb-16 md:last-of-type:mb-24 text-primary-400',
                headingStyle: true,
              },
            }}
            content={heading}
          />
        </div>
        {!!subheading && (
          <div className="mx-auto md:w-[55%] [&_p]:text-lg md:[&_p]:text-xl [&_p_strong]:font-bold [&_p_strong]:text-primary-350">
            {typeof subheading === 'string' ? <p>{subheading}</p> : <CustomPortableText content={subheading} />}
          </div>
        )}
      </div>
    </Container>

    <TiersCompareTable data={tiers} queryParams={queryParams} toggleBenefits={toggleBenefits} category={pageName} />

    {showAccountBuilderCta && <AccountBuilderCTA />}
  </Section>
);

export default Tiers;

const AccountBuilderCTA = () => {
  const locale = useContext(LocaleContext);
  return (
    <Section>
      <Container className="px-32 py-40 text-center xs:px-0 md:py-64">
        <P className="mb-32 text-lg text-grey-500 md:text-xl">
          {
            {
              en: 'Not sure which one speaks to you? Take our quiz to find the best plan for you.',
              fr: 'Vous ne savez pas lequel vous convient? Faites notre quiz pour trouver le meilleur plan pour vous.',
            }[locale]
          }
        </P>
        <Link
          to={`${locale === 'fr' ? '/fr' : ''}/account-builder-quiz`}
          onClick={() => trackEvent('Clicked', { name: 'find-a-plan' })}
        >
          <Button size="lg" tabIndex={-1} variant="primary">
            {{ en: 'Find a plan', fr: 'Trouver un plan' }[locale]}
          </Button>
        </Link>
      </Container>
    </Section>
  );
};
